import { z } from 'zod';
import { uuidSchema, localeSchema } from './index';

// changelog: { [locale]: string[] } — bullets por idioma.
// Fallback en el frontend: primero el locale del user, luego 'es'.
export const changelogSchema = z.record(localeSchema, z.array(z.string().min(1).max(500)));
export type Changelog = z.infer<typeof changelogSchema>;

export const releaseSchema = z.object({
  id: uuidSchema,
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  published_at: z.string().nullable(),
  changelog: changelogSchema,
  created_by: uuidSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Release = z.infer<typeof releaseSchema>;

export const releaseCreateInputSchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  changelog: changelogSchema,
  publish_now: z.boolean().default(false),
});
export type ReleaseCreateInput = z.infer<typeof releaseCreateInputSchema>;

// system_messages
export const systemMessageKindSchema = z.enum(['info', 'warning', 'maintenance']);
export type SystemMessageKind = z.infer<typeof systemMessageKindSchema>;

export const systemMessageAudienceSchema = z.enum(['all', 'authenticated', 'admins']);
export type SystemMessageAudience = z.infer<typeof systemMessageAudienceSchema>;

// title/body: mismo shape que changelog pero un solo string por locale.
export const localizedTextSchema = z.record(localeSchema, z.string().min(1).max(500));
export type LocalizedText = z.infer<typeof localizedTextSchema>;

export const systemMessageSchema = z.object({
  id: uuidSchema,
  kind: systemMessageKindSchema,
  title: localizedTextSchema,
  body: localizedTextSchema,
  audience: systemMessageAudienceSchema,
  active_from: z.string(),
  active_until: z.string().nullable(),
  dismissible: z.boolean(),
  created_by: uuidSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SystemMessage = z.infer<typeof systemMessageSchema>;

export const systemMessageCreateInputSchema = z.object({
  kind: systemMessageKindSchema,
  title: localizedTextSchema,
  body: localizedTextSchema,
  audience: systemMessageAudienceSchema.default('all'),
  active_from: z.string().optional(),
  active_until: z.string().nullable().optional(),
  dismissible: z.boolean().default(true),
});
export type SystemMessageCreateInput = z.infer<typeof systemMessageCreateInputSchema>;

// Helper: pick localized text from a JSONB blob with fallback chain.
// preferredLocale → 'es' → primer key disponible → ''
export function pickLocalized(
  blob: LocalizedText | undefined | null,
  preferredLocale: string,
): string {
  if (!blob) return '';
  const preferred = blob[preferredLocale as keyof LocalizedText];
  if (preferred) return preferred;
  const es = blob.es;
  if (es) return es;
  const first = Object.values(blob).find(Boolean);
  return first ?? '';
}

export function pickChangelog(
  blob: Changelog | undefined | null,
  preferredLocale: string,
): string[] {
  if (!blob) return [];
  const preferred = blob[preferredLocale as keyof Changelog];
  if (preferred && preferred.length > 0) return preferred;
  const es = blob.es;
  if (es && es.length > 0) return es;
  const first = Object.values(blob).find((arr) => arr && arr.length > 0);
  return first ?? [];
}
